import { useState, useEffect, useContext } from "react";
import {
  Form,
  Button,
  Container,
  Card,
  Row,
  Col,
  Alert,
  Spinner,
} from "react-bootstrap";
import { Notyf } from "notyf";
import { useNavigate } from "react-router-dom";
import UserContext from "../context/UserContext";

function CreateProduct() {
  const navigate = useNavigate();
  const notyf = new Notyf();
  const { user } = useContext(UserContext);
  const [productName, setProductName] = useState("");
  const [productDescription, setProductDescription] = useState("");
  const [productPrice, setProductPrice] = useState("");
  const [isBtnActive, setIsBtnActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!productName.trim()) {
      newErrors.productName = "Product name is required";
    } else if (productName.trim().length < 2) {
      newErrors.productName = "Product name must be at least 2 characters";
    }

    if (!productDescription.trim()) {
      newErrors.productDescription = "Description is required";
    } else if (productDescription.trim().length < 10) {
      newErrors.productDescription =
        "Description must be at least 10 characters";
    }

    if (!productPrice || productPrice <= 0) {
      newErrors.productPrice = "Price must be greater than 0";
    } else if (productPrice > 1000000) {
      newErrors.productPrice = "Price cannot exceed ₱1,000,000";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/products`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            name: productName.trim(),
            description: productDescription.trim(),
            price: parseFloat(productPrice),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create product");
      }

      await response.json();
      notyf.success("Product created successfully!");
      navigate("/products");

      // Reset Form
      setProductName("");
      setProductDescription("");
      setProductPrice("");
      setIsBtnActive(false);
      setErrors({});
    } catch (error) {
      console.error("Error creating product:", error);
      notyf.error("Failed to create product. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (productName.trim() && productDescription.trim() && productPrice > 0) {
      setIsBtnActive(true);
    } else {
      setIsBtnActive(false);
    }

    // Clear errors when user starts typing
    if (Object.keys(errors).length > 0) {
      const newErrors = { ...errors };
      if (productName.trim() && errors.productName) {
        delete newErrors.productName;
      }
      if (productDescription.trim() && errors.productDescription) {
        delete newErrors.productDescription;
      }
      if (productPrice > 0 && errors.productPrice) {
        delete newErrors.productPrice;
      }
      setErrors(newErrors);
    }
  }, [productDescription, productName, productPrice, errors]);

  return (
    <div className="create-product-wrapper">
      {/* Admin Hero Section */}
      <div className="admin-hero-section">
        <Container>
          <div className="text-center py-5">
            <div className="hero-icon-container mx-auto mb-4">
              <i className="fas fa-plus-circle text-white"></i>
            </div>
            <h1 className="display-5 fw-bold text-white mb-3">
              Create New Product
            </h1>
            <p className="lead text-white-50 mb-4">
              Add a new product to your inventory
            </p>
            <div className="admin-badge">
              <i className="fas fa-shield-alt me-2"></i>
              Administrator Panel
            </div>
          </div>
        </Container>
      </div>

      {/* Create Product Form */}
      <Container className="create-product-content">
        <Row className="justify-content-center">
          <Col lg={8} xl={7}>
            <Card className="create-product-card border-0 shadow-lg">
              <Card.Header className="admin-card-header border-0">
                <div className="d-flex align-items-center">
                  <div className="header-icon me-3">
                    <i className="fas fa-box text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-white mb-0 fw-bold">
                      Product Information
                    </h4>
                    <small className="text-white-50">
                      Fill in the details for your new product
                    </small>
                  </div>
                </div>
              </Card.Header>

              <Card.Body className="p-5">
                <Form onSubmit={handleSubmit} noValidate>
                  <Row className="g-4">
                    <Col md={12}>
                      <div className="form-field">
                        <Form.Label className="field-label">
                          <i className="fas fa-tag me-2"></i>
                          Product Name *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter product name (e.g., Premium Coffee Beans)"
                          value={productName}
                          onChange={(e) => setProductName(e.target.value)}
                          className={`enhanced-input ${
                            errors.productName ? "is-invalid" : ""
                          }`}
                          disabled={isLoading}
                        />
                        {errors.productName && (
                          <div className="invalid-feedback d-block">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {errors.productName}
                          </div>
                        )}
                        <div className="character-count">
                          {productName.length}/100
                        </div>
                      </div>
                    </Col>

                    <Col md={12}>
                      <div className="form-field">
                        <Form.Label className="field-label">
                          <i className="fas fa-align-left me-2"></i>
                          Product Description *
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          placeholder="Describe your product in detail (features, benefits, specifications...)"
                          value={productDescription}
                          onChange={(e) =>
                            setProductDescription(e.target.value)
                          }
                          className={`enhanced-textarea ${
                            errors.productDescription ? "is-invalid" : ""
                          }`}
                          disabled={isLoading}
                        />
                        {errors.productDescription && (
                          <div className="invalid-feedback d-block">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {errors.productDescription}
                          </div>
                        )}
                        <div className="character-count">
                          {productDescription.length}/500
                        </div>
                      </div>
                    </Col>

                    <Col md={12}>
                      <div className="form-field">
                        <Form.Label className="field-label">
                          <i className="fas fa-peso-sign me-2"></i>
                          Price (₱) *
                        </Form.Label>
                        <div className="price-input-wrapper">
                          <span className="currency-symbol">₱</span>
                          <Form.Control
                            type="number"
                            placeholder="0.00"
                            value={productPrice}
                            onChange={(e) => setProductPrice(e.target.value)}
                            className={`enhanced-input price-input ${
                              errors.productPrice ? "is-invalid" : ""
                            }`}
                            min="0"
                            step="0.01"
                            disabled={isLoading}
                          />
                        </div>
                        {errors.productPrice && (
                          <div className="invalid-feedback d-block">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {errors.productPrice}
                          </div>
                        )}
                        <small className="form-text text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          Enter the price in Philippine Pesos
                        </small>
                      </div>
                    </Col>
                  </Row>

                  <hr className="my-4" />

                  <div className="form-actions">
                    <div className="d-flex justify-content-between align-items-center">
                      <Button
                        variant="outline-secondary"
                        onClick={() => navigate("/products")}
                        className="cancel-btn"
                        disabled={isLoading}
                      >
                        <i className="fas fa-times me-2"></i>
                        Cancel
                      </Button>

                      <Button
                        type="submit"
                        disabled={!isBtnActive || isLoading}
                        className="create-btn"
                      >
                        {isLoading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Creating...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-plus me-2"></i>
                            Create Product
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        .create-product-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          position: relative;
          padding-bottom: 3rem;
        }

        .admin-hero-section {
          background: linear-gradient(
            135deg,
            #6f42c1 0%,
            #8e5cc6 25%,
            #a070d1 50%,
            #8e5cc6 75%,
            #495057 100%
          );
          position: relative;
          overflow: hidden;
          padding: 2rem 0;
        }

        .admin-hero-section::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M20 20c0 11.046-8.954 20-20 20v20h40V20c0-11.046-8.954-20-20-20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
            repeat;
          animation: float 20s ease-in-out infinite;
        }

        .hero-icon-container {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          animation: pulse-glow 2s ease-in-out infinite alternate;
        }

        .hero-icon-container i {
          font-size: 2.5rem;
        }

        .admin-badge {
          display: inline-flex;
          align-items: center;
          background: rgba(255, 255, 255, 0.15);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          animation: shimmer 3s ease-in-out infinite;
        }

        .create-product-content {
          margin-top: -3rem;
          position: relative;
          z-index: 10;
        }

        .create-product-card {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          overflow: hidden;
          animation: slideUpFade 0.6s ease-out;
        }

        .admin-card-header {
          background: linear-gradient(135deg, #6f42c1 0%, #8e5cc6 100%);
          padding: 2rem;
          position: relative;
          overflow: hidden;
        }

        .admin-card-header::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            45deg,
            transparent 40%,
            rgba(255, 255, 255, 0.1) 50%,
            transparent 60%
          );
          animation: shine 3s ease-in-out infinite;
        }

        .header-icon {
          width: 50px;
          height: 50px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .form-field {
          margin-bottom: 2rem;
          position: relative;
        }

        .field-label {
          font-weight: 600;
          color: #495057;
          margin-bottom: 0.8rem;
          font-size: 1rem;
          display: flex;
          align-items: center;
        }

        .field-label i {
          color: #6f42c1;
          width: 20px;
        }

        .enhanced-input,
        .enhanced-textarea {
          border: 2px solid #e9ecef;
          border-radius: 12px;
          padding: 0.875rem 1rem;
          font-size: 1rem;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          background: #fff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
        }

        .enhanced-input:focus,
        .enhanced-textarea:focus {
          border-color: #6f42c1;
          box-shadow: 0 0 0 3px rgba(111, 66, 193, 0.1),
            0 4px 12px rgba(111, 66, 193, 0.15);
          transform: translateY(-1px);
        }

        .enhanced-input:disabled,
        .enhanced-textarea:disabled {
          background: #f8f9fa;
          opacity: 0.7;
        }

        .price-input-wrapper {
          position: relative;
        }

        .currency-symbol {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #6c757d;
          font-weight: 600;
          z-index: 5;
        }

        .price-input {
          padding-left: 2.5rem !important;
        }

        .character-count {
          position: absolute;
          right: 0.75rem;
          bottom: -1.5rem;
          font-size: 0.75rem;
          color: #6c757d;
        }

        .form-actions {
          margin-top: 1.5rem;
        }

        .cancel-btn {
          border: 2px solid #6c757d;
          color: #6c757d;
          font-weight: 600;
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          transition: all 0.3s ease;
        }

        .cancel-btn:hover {
          background: #6c757d;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
        }

        .create-btn {
          background: linear-gradient(135deg, #6f42c1 0%, #8e5cc6 100%);
          border: none;
          color: white;
          font-weight: 600;
          padding: 0.75rem 2rem;
          border-radius: 12px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(111, 66, 193, 0.3);
        }

        .create-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(111, 66, 193, 0.4);
          background: linear-gradient(135deg, #8e5cc6 0%, #6f42c1 100%);
        }

        .create-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
          box-shadow: 0 2px 8px rgba(111, 66, 193, 0.2);
        }

        .invalid-feedback {
          display: flex;
          align-items: center;
          color: #dc3545;
          font-size: 0.875rem;
          margin-top: 0.5rem;
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes pulse-glow {
          0% {
            box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
            transform: scale(1);
          }
          100% {
            box-shadow: 0 0 30px rgba(255, 255, 255, 0.5);
            transform: scale(1.05);
          }
        }

        @keyframes shimmer {
          0%,
          100% {
            background: rgba(255, 255, 255, 0.15);
          }
          50% {
            background: rgba(255, 255, 255, 0.25);
          }
        }

        @keyframes slideUpFade {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shine {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @media (max-width: 768px) {
          .admin-hero-section {
            padding: 1.5rem 0;
          }

          .hero-icon-container {
            width: 60px;
            height: 60px;
          }

          .hero-icon-container i {
            font-size: 2rem;
          }

          .create-product-content {
            margin-top: -2rem;
          }

          .admin-card-header {
            padding: 1.5rem;
          }

          .form-actions .d-flex {
            flex-direction: column-reverse;
            gap: 1rem;
          }

          .create-btn,
          .cancel-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}

export default CreateProduct;
